import { useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiError, fileUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, UserPlus, ListNumbers, RadioButton, UploadSimple, SpinnerGap, LockSimple } from "@phosphor-icons/react";

function PositionDialog({ electionId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", ballot_type: "single_choice", max_selections: 1 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/elections/${electionId}/positions`, { ...f, max_selections: Number(f.max_selections) });
      toast.success("Position added");
      setOpen(false); setF({ title: "", description: "", ballot_type: "single_choice", max_selections: 1 });
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-position-btn" className="inline-flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-md font-semibold text-sm hover:bg-zinc-800 transition-colors active:scale-95">
          <Plus size={18} weight="bold" /> Add position
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Add position</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Title</Label>
            <Input data-testid="position-title" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="President" className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Description</Label>
            <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Ballot type</Label>
            <Select value={f.ballot_type} onValueChange={(v) => setF({ ...f, ballot_type: v })}>
              <SelectTrigger data-testid="ballot-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="single_choice">Single choice (pick one)</SelectItem>
                <SelectItem value="ranked">Ranked (drag to rank · Borda)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button data-testid="save-position" className="w-full h-11 bg-[#1F6FE5] text-white rounded-md font-semibold hover:bg-[#1552C9] transition-colors active:scale-[0.98]">Add position</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CandidateDialog({ positionId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", bio: "", photo_url: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, photo_url: data.url }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/positions/${positionId}/candidates`, f);
      toast.success("Candidate added");
      setOpen(false); setF({ name: "", bio: "", photo_url: "" });
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`add-candidate-${positionId}`} className="inline-flex items-center gap-1.5 text-sm text-[#1F6FE5] font-medium hover:underline">
          <UserPlus size={16} weight="bold" /> Add candidate
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">Add candidate</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0">
              {f.photo_url ? <img src={fileUrl(f.photo_url)} alt="" className="w-full h-full object-cover" /> : <UserPlus size={22} className="text-zinc-300" />}
            </div>
            <div>
              <input ref={fileRef} data-testid="candidate-photo-input" type="file" accept="image/*" onChange={upload} className="hidden" />
              <button type="button" onClick={() => fileRef.current.click()} disabled={uploading} className="inline-flex items-center gap-2 px-3 py-2 border border-zinc-300 rounded-md text-sm hover:border-black transition-colors">
                {uploading ? <SpinnerGap size={16} className="animate-spin" /> : <UploadSimple size={16} />} Upload photo
              </button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Name</Label>
            <Input data-testid="candidate-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Bio</Label>
            <Textarea data-testid="candidate-bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className="mt-1.5" />
          </div>
          <button data-testid="save-candidate" className="w-full h-11 bg-[#1F6FE5] text-white rounded-md font-semibold hover:bg-[#1552C9] transition-colors active:scale-[0.98]">Add candidate</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function PositionsTab({ electionId, positions, status, onChange }) {
  const locked = status === "closed";

  const delPosition = async (id) => {
    try { await api.delete(`/positions/${id}`); toast.success("Position removed"); onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delCandidate = async (id) => {
    try { await api.delete(`/candidates/${id}`); toast.success("Candidate removed"); onChange(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">{locked ? <span className="inline-flex items-center gap-1.5 text-zinc-500"><LockSimple size={15} weight="fill" /> Structure is locked once the election has closed.</span> : (status === "live" ? "Editing a live election — changes are recorded in the audit log." : "Define the positions and candidates on the ballot.")}</p>
        {!locked && <PositionDialog electionId={electionId} onDone={onChange} />}
      </div>

      {positions.length === 0 ? (
        <div data-testid="empty-positions" className="border border-dashed border-zinc-300 bg-white p-14 text-center text-zinc-500">No positions yet. Add one to build your ballot.</div>
      ) : (
        <div className="space-y-4">
          {positions.map((p) => (
            <div key={p.id} data-testid={`position-${p.id}`} className="bg-white border border-zinc-200">
              <div className="flex items-start justify-between p-5 border-b border-zinc-100">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-lg tracking-tight">{p.title}</h3>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-sm">
                      {p.ballot_type === "ranked" ? <><ListNumbers size={12} /> Ranked</> : <><RadioButton size={12} /> Single</>}
                    </span>
                  </div>
                  {p.description && <p className="text-sm text-zinc-500 mt-0.5">{p.description}</p>}
                </div>
                {!locked && (
                  <button data-testid={`del-position-${p.id}`} onClick={() => delPosition(p.id)} className="text-zinc-300 hover:text-red-500 transition-colors"><Trash size={18} /></button>
                )}
              </div>
              <div className="p-5 space-y-3">
                {(p.candidates || []).map((c) => (
                  <div key={c.id} data-testid={`candidate-${c.id}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0 text-sm font-bold text-zinc-400">
                      {c.photo_url ? <img src={fileUrl(c.photo_url)} alt="" className="w-full h-full object-cover" /> : c.name[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        {c.name}
                        {c.needs_photo && !c.photo_url && <span data-testid={`needs-photo-${c.id}`} title="Promoted from a nomination — add a photo" className="text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm">Needs photo</span>}
                      </div>
                      {c.bio && <div className="text-xs text-zinc-500 truncate">{c.bio}</div>}
                    </div>
                    {!locked && <button data-testid={`del-candidate-${c.id}`} onClick={() => delCandidate(c.id)} className="text-zinc-300 hover:text-red-500 transition-colors"><Trash size={16} /></button>}
                  </div>
                ))}
                {(p.candidates || []).length === 0 && <p className="text-sm text-zinc-400">No candidates yet.</p>}
                {!locked && <div className="pt-1"><CandidateDialog positionId={p.id} onDone={onChange} /></div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
